/**
 * Pure helpers behind the terrain tools: brush falloff curves (same math as
 * prism_terrain::brush), the scene commands of a tree stroke
 * (`terrainTrees` engine event → one `batch` = one undo step), and
 * neighbouring tiles (new tile data + entity + links).
 */

import { assetDisplayName, toAssetValue, MESH_SOURCE_SPEC, type AssetSpec } from '../../lib/asset-types.js';
import { slug, uniqueWith, type EditorCommand, type EntityDesc, type SceneDoc, type Vec3 } from '../../lib/scene-doc.js';
import { composeMatrix, decomposeMatrix, invertAffine, multiply, worldMatrix } from '../../lib/transform-math.js';
import type { TerrainJson } from './terrain-store.js';

// ---------------------------------------------------------------- falloff

export const FALLOFF_PRESETS = ['smooth', 'linear', 'sharp', 'sphere', 'constant'] as const;
export type FalloffPreset = (typeof FALLOFF_PRESETS)[number];
/** Preset name or `[t, value]` points (t = 0 center … 1 edge). */
export type Falloff = FalloffPreset | Array<[number, number]>;

/** Piecewise-linear curve through `[t, value]` points (sorted; clamped). No points = 1. */
export function curveEval(points: ReadonlyArray<readonly [number, number]>, t: number): number {
  const pts = points.filter((p) => Number.isFinite(p[0]) && Number.isFinite(p[1])).slice().sort((a, b) => a[0] - b[0]);
  if (pts.length === 0) return 1;
  const first = pts[0]!;
  const last = pts[pts.length - 1]!;
  let v: number;
  if (t <= first[0]) v = first[1];
  else if (t >= last[0]) v = last[1];
  else {
    let k = 0;
    while (k < pts.length - 2 && t > pts[k + 1]![0]) k += 1;
    const a = pts[k]!;
    const b = pts[k + 1]!;
    const span = b[0] - a[0];
    v = span <= 1e-9 ? b[1] : a[1] + ((b[1] - a[1]) * (t - a[0])) / span;
  }
  return Math.min(1, Math.max(0, v));
}

const smooth01 = (t: number): number => {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
};

/** Brush strength (0..1) at `t` = distance / radius (prism_terrain `Falloff::eval`). */
export function evalFalloff(falloff: Falloff, t: number): number {
  const x = Math.min(1, Math.max(0, t));
  if (Array.isArray(falloff)) return curveEval(falloff, x);
  switch (falloff) {
    case 'smooth':
      return smooth01(1 - x);
    case 'linear':
      return 1 - x;
    case 'sharp':
      return (1 - x) * (1 - x);
    case 'sphere':
      return Math.sqrt(Math.max(0, 1 - x * x));
    case 'constant':
      return 1;
  }
}

/** A preset sampled as editable curve points. */
export function presetPoints(preset: FalloffPreset, n = 5): Array<[number, number]> {
  return Array.from({ length: n }, (_, i) => {
    const t = i / (n - 1);
    return [round3(t), round3(evalFalloff(preset, t))] as [number, number];
  });
}

/** SVG polyline points (`w × h` box, value 1 at the top) of a falloff. */
export function falloffPath(falloff: Falloff, w: number, h: number, samples = 32): string {
  const out: string[] = [];
  for (let i = 0; i <= samples; i += 1) {
    const t = i / samples;
    out.push(`${round3(t * w)},${round3((1 - evalFalloff(falloff, t)) * h)}`);
  }
  return out.join(' ');
}

const round3 = (v: number): number => Math.round(v * 1000) / 1000;

// ---------------------------------------------------------------- stamps

export const BUILTIN_STAMPS = ['hill', 'crater', 'mesa', 'ridge', 'volcano', 'cone', 'terraces'] as const;
export const STAMP_BLENDS = ['add', 'max', 'min', 'set'] as const;

/** Built-in stamp profile at (u, v) in -1..1 (prism_terrain `builtin_stamp`), for previews. */
export function stampValue(name: string, u: number, v: number): number {
  const r = Math.hypot(u, v);
  const bump = r >= 1 ? 0 : 0.5 + 0.5 * Math.cos(Math.PI * r);
  switch (name) {
    case 'hill':
      return bump;
    case 'crater':
      if (r >= 1) return 0;
      return r < 0.7 ? -1 + 1.35 * smooth01(r / 0.7) : 0.35 * smooth01((1 - r) / 0.3);
    case 'mesa':
      return smooth01((0.75 - r) / 0.2);
    case 'ridge': {
      const across = smooth01(1 - Math.abs(v) / 0.4);
      const along = Math.abs(u) >= 1 ? 0 : 0.5 + 0.5 * Math.cos(Math.PI * Math.abs(u));
      return across * Math.sqrt(along);
    }
    case 'volcano':
      if (r >= 1) return 0;
      return r < 0.18 ? 0.65 + 0.2 * (r / 0.18) : 0.85 * Math.pow(smooth01((1 - r) / 0.82), 0.8);
    case 'cone':
      return Math.max(0, 1 - r);
    case 'terraces': {
      const k = Math.floor(bump * 4);
      return (k + smooth01((bump * 4 - k - 0.7) / 0.3)) / 4;
    }
    default:
      return 0;
  }
}

// ---------------------------------------------------------------- trees

/** Tree prototypes: prefabs or models. */
export const TREE_SPEC: AssetSpec = {
  suffix: ['.prefab.json', '.model.json', '.glb', '.gltf'],
  prefixes: MESH_SOURCE_SPEC.prefixes,
  label: 'Tree',
};

/** Tag of the group entity holding a terrain's painted trees. */
export const TREE_GROUP_TAG = 'terrainTrees';

/** `terrainTrees` engine event (end of a tree stroke; world space). */
export interface TreeStrokeEvent {
  terrain: string;
  group: string | null;
  add: Array<{ source: string; position: Vec3; rotation: Vec3; scale: Vec3 }>;
  remove: string[];
}

function tagsOf(e: EntityDesc): string[] {
  const t = e.components?.tags;
  return Array.isArray(t) ? t.filter((x): x is string => typeof x === 'string') : [];
}

/** The group entity of `terrainId`'s trees: a child of the terrain tagged `terrainTrees`. */
export function findTreeGroup(doc: SceneDoc, terrainId: string): string | null {
  return doc.entities.find((e) => e.parent === terrainId && tagsOf(e).includes(TREE_GROUP_TAG))?.id ?? null;
}

/** Components of a tree instance: a prefab reference, else a mesh. */
export function treeComponents(source: string): Record<string, unknown> {
  const path = source.replace(/^(model|gltf):/, '');
  if (path.endsWith('.prefab.json')) return { prefab: { path } };
  return { mesh: { source: /^(model|gltf|primitive):/.test(source) ? source : toAssetValue(MESH_SOURCE_SPEC, source) } };
}

/** Points of `candidates` at least `minDistance` from `existing` and from each other (XZ). */
export function filterMinDistance(candidates: readonly Vec3[], existing: readonly Vec3[], minDistance: number): number[] {
  const kept: Vec3[] = [];
  const out: number[] = [];
  const d2 = minDistance * minDistance;
  const far = (p: Vec3, list: readonly Vec3[]) => list.every((q) => (p[0] - q[0]) ** 2 + (p[2] - q[2]) ** 2 >= d2 - 1e-9);
  candidates.forEach((p, i) => {
    if (minDistance <= 0 || (far(p, existing) && far(p, kept))) {
      kept.push(p);
      out.push(i);
    }
  });
  return out;
}

/** World position of entity `id`. */
function worldPos(doc: SceneDoc, id: string): Vec3 {
  const m = worldMatrix(doc, id);
  return [m[12]!, m[13]!, m[14]!];
}

/**
 * Scene commands for a tree stroke: the group entity (created under the
 * terrain the first time), one entity per placed tree (transform converted
 * into the group's space; placements closer than `minDistance` to trees
 * already in the document are dropped) and deletions. Apply them as one
 * batch (one undo step).
 */
export function treeStrokeCommands(
  doc: SceneDoc,
  event: TreeStrokeEvent,
  minDistance = 0,
): { commands: EditorCommand[]; group: string | null } {
  const terrain = doc.entities.find((e) => e.id === event.terrain);
  if (!terrain) return { commands: [], group: null };
  const commands: EditorCommand[] = [];
  let group = event.group && doc.entities.some((e) => e.id === event.group) ? event.group : findTreeGroup(doc, terrain.id);
  const children = group ? doc.entities.filter((e) => e.parent === group) : [];
  for (const id of event.remove) {
    if (children.some((c) => c.id === id)) commands.push({ cmd: 'deleteEntity', id });
  }
  const removed = new Set(event.remove);
  const existing = children.filter((c) => !removed.has(c.id)).map((c) => worldPos(doc, c.id));
  const keep = filterMinDistance(
    event.add.map((a) => a.position),
    existing,
    minDistance,
  );
  if (keep.length === 0) return { commands, group };
  let parentWorld = worldMatrix(doc, terrain.id);
  if (!group) {
    group = uniqueWith(slug(`${terrain.id}_trees`), (c) => doc.entities.some((e) => e.id === c));
    commands.push({
      cmd: 'addEntity',
      desc: { id: group, name: 'Trees', parent: terrain.id, transform: {}, components: { tags: [TREE_GROUP_TAG] } },
      parent: terrain.id,
    });
  } else {
    parentWorld = worldMatrix(doc, group);
  }
  const toLocal = invertAffine(parentWorld);
  for (const i of keep) {
    const a = event.add[i]!;
    const local = decomposeMatrix(multiply(toLocal, composeMatrix({ position: a.position, rotation: a.rotation, scale: a.scale })));
    commands.push({
      cmd: 'addEntity',
      desc: { id: '', name: assetDisplayName(a.source.replace(/^(model|gltf):/, '')) || 'Tree', parent: group, transform: local, components: treeComponents(a.source) },
      parent: group,
    });
  }
  return { commands, group };
}

// ---------------------------------------------------------------- neighbours

export const SIDES = ['left', 'right', 'back', 'front'] as const;
export type Side = (typeof SIDES)[number];

export const OPPOSITE: Record<Side, Side> = { left: 'right', right: 'left', back: 'front', front: 'back' };

/** Grid step of a side (left = -X, right = +X, back = -Z, front = +Z). */
export const SIDE_STEP: Record<Side, [number, number]> = { left: [-1, 0], right: [1, 0], back: [0, -1], front: [0, 1] };

export type Neighbors = Partial<Record<Side, string>>;

export function neighborsOf(value: Record<string, unknown> | undefined): Neighbors {
  const n = value?.neighbors;
  const out: Neighbors = {};
  if (n && typeof n === 'object' && !Array.isArray(n)) {
    for (const s of SIDES) {
      const v = (n as Record<string, unknown>)[s];
      if (typeof v === 'string' && v) out[s] = v;
    }
  }
  return out;
}

/** A new tile's data: same settings, layers and details as `json`, flat, without paint or holes. */
export function neighborTerrainJson(json: TerrainJson): TerrainJson {
  const {
    heights: _h,
    chunks: _c,
    holes: _x,
    heightmap: _i,
    ...rest
  } = json;
  return {
    ...rest,
    layers: (json.layers ?? []).map(({ weights: _w, ...l }) => l),
    details: (json.details ?? []).map(({ map: _m, ...d }) => d),
  };
}

/** Terrain entities with a terrain component and their world positions. */
function terrainTiles(doc: SceneDoc): Array<{ id: string; pos: Vec3; value: Record<string, unknown> }> {
  return doc.entities
    .filter((e) => e.components?.terrain && typeof e.components.terrain === 'object')
    .map((e) => ({ id: e.id, pos: worldPos(doc, e.id), value: e.components!.terrain as Record<string, unknown> }));
}

/** Data file path for a new tile next to `path` (`island.terrain.json` → `island_right.terrain.json`, unique). */
export function neighborPath(path: string, side: Side, taken: (p: string) => boolean): string {
  const base = path.endsWith('.terrain.json') ? path.slice(0, -'.terrain.json'.length) : path;
  return uniqueWith(`${base}_${side}`, (c) => taken(`${c}.terrain.json`)) + '.terrain.json';
}

/**
 * Commands creating the tile on `side` of terrain entity `id` (size `size`
 * meters, data file `dataPath`): the new terrain entity (same component
 * settings, placed next to it, a sibling) and the neighbour links of every
 * tile it touches (`setComponent` on each). One batch = one undo step.
 */
export function createNeighborCommands(
  doc: SceneDoc,
  id: string,
  side: Side,
  size: [number, number],
  dataPath: string,
): { commands: EditorCommand[]; newId: string } | { error: string } {
  const source = doc.entities.find((e) => e.id === id);
  const value = source?.components?.terrain as Record<string, unknown> | undefined;
  if (!source || !value) return { error: `${id} has no terrain component` };
  if (neighborsOf(value)[side]) return { error: `${id} already has a ${side} neighbour` };
  const tiles = terrainTiles(doc);
  const origin = worldPos(doc, id);
  const [sx, sz] = SIDE_STEP[side];
  const pos: Vec3 = [origin[0] + sx * size[0], origin[1], origin[2] + sz * size[1]];
  const near = (a: Vec3, b: Vec3) => Math.abs(a[0] - b[0]) < 1e-3 && Math.abs(a[2] - b[2]) < 1e-3;
  if (tiles.some((t) => near(t.pos, pos))) return { error: `a terrain already sits on the ${side} of ${id}` };
  const newId = uniqueWith(slug(`${id}_${side}`), (c) => doc.entities.some((e) => e.id === c));
  // Links of the new tile: every terrain on one of its four sides.
  const links: Neighbors = {};
  const updates: EditorCommand[] = [];
  for (const s of SIDES) {
    const [dx, dz] = SIDE_STEP[s];
    const at: Vec3 = [pos[0] + dx * size[0], pos[1], pos[2] + dz * size[1]];
    const other = tiles.find((t) => near(t.pos, at));
    if (!other || neighborsOf(other.value)[OPPOSITE[s]]) continue;
    links[s] = other.id;
    updates.push({
      cmd: 'setComponent',
      id: other.id,
      key: 'terrain',
      value: { ...other.value, neighbors: { ...neighborsOf(other.value), [OPPOSITE[s]]: newId } },
    });
  }
  // Same parent as the source; position in the parent's space.
  const parentWorld = source.parent ? worldMatrix(doc, source.parent) : null;
  const local = parentWorld
    ? decomposeMatrix(multiply(invertAffine(parentWorld), composeMatrix({ position: pos })))
    : { position: pos, rotation: [0, 0, 0] as Vec3, scale: [1, 1, 1] as Vec3 };
  const component: Record<string, unknown> = { ...value, data: dataPath, neighbors: links };
  const commands: EditorCommand[] = [
    {
      cmd: 'addEntity',
      desc: { id: newId, name: `${source.name ?? id} ${side}`, parent: source.parent ?? null, transform: { position: local.position }, components: { terrain: component } },
      parent: source.parent ?? null,
    },
    ...updates,
  ];
  return { commands, newId };
}

/** Mini map cells around terrain `id`: grid offsets (-1..1) → tile id (or null). */
export function neighborGrid(doc: SceneDoc, id: string, size: [number, number]): Array<{ dx: number; dz: number; id: string | null }> {
  const tiles = terrainTiles(doc);
  const origin = worldPos(doc, id);
  const out: Array<{ dx: number; dz: number; id: string | null }> = [];
  for (let dz = 1; dz >= -1; dz -= 1) {
    for (let dx = -1; dx <= 1; dx += 1) {
      const at: Vec3 = [origin[0] + dx * size[0], origin[1], origin[2] + dz * size[1]];
      const t = tiles.find((x) => Math.abs(x.pos[0] - at[0]) < 1e-3 && Math.abs(x.pos[2] - at[2]) < 1e-3);
      out.push({ dx, dz, id: t?.id ?? null });
    }
  }
  return out;
}
